/**
 * Utilitários para formatação de dados
 */

export function formatarData(data: string | Date | null | undefined): string {
  if (!data) return '-';
  let date: Date;
  if (data instanceof Date) {
    date = data;
  } else {
    const dataString = String(data);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dataString)) {
      date = new Date(dataString + 'T00:00:00');
    } else {
      date = new Date(dataString.replace(' ', 'T'));
    }
  }
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
}

export function formatarDataHora(dataString: string): string {
  const data = new Date(dataString);
  return data.toLocaleDateString('pt-BR') + ' ' + data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatarDataHoraSegundos(dataString: string): string {
  const data = new Date(dataString);
  return data.toLocaleDateString('pt-BR') + ' ' + data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

/**
 * Retorna a data e hora local no formato YYYY-MM-DDTHH:mm para inputs do tipo datetime-local
 */
export const getLocalDateTimeString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function formatarDuracaoTotal(seconds: number): string {
  if (!seconds) return '0s';

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
}

export function formatarValor(valor: number): string {
  if (isNaN(valor) || !isFinite(valor)) {
    return 'R$ 0,00';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor);
}

//formatação de valor durante a digitação em um campo de texto
export const formatarValorOnInput = (valor: string): string => {
  // Remove tudo que não é número
  const apenasNumeros = valor.replace(/\D/g, '');

  if (apenasNumeros === '') return '0,00';

  // Converte para número e divide por 100 para considerar os centavos
  const numero = parseInt(apenasNumeros) / 100;

  // Formata como moeda brasileira
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/** Converte string formatada (ex: "1.234,56") para número */
export const parsearValorFormatado = (valorFormatado: string): number => {
  const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
  const numeroComPonto = numeroLimpo.replace(',', '.');
  return parseFloat(numeroComPonto) || 0;
};

/** Formata número para exibição em campo de input (2 casas) */
export const formatarMoedaParaInput = (valor: number): string => {
  return (valor || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Formatação de valor por litro durante digitação (3 casas decimais) */
export const formatarValorLitroOnInput = (valor: string): string => {
  const apenasNumeros = valor.replace(/\D/g, '');
  if (apenasNumeros === '') return '0,000';
  const numero = parseInt(apenasNumeros, 10) / 1000;
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

/** Formata número para exibição em campo de valor por litro (3 casas) */
export const formatarValorLitroParaInput = (valor: number): string => {
  return (valor || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

// Formatação de porcentagem para exibição
export function formatarPorcentagem(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '';

  const numero = typeof valor === 'string' ? parseFloat(valor) : valor;

  if (isNaN(numero)) return '';

  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// Formatação de porcentagem durante a digitação (sem separador de milhar)
export const formatarPorcentagemOnInput = (valor: string): string => {
  // Remove tudo que não é número
  const apenasNumeros = valor.replace(/\D/g, '');

  if (apenasNumeros === '') return '0,00';

  // Converte para número e divide por 100 para considerar os centavos
  let numero = parseInt(apenasNumeros) / 100;

  // Limita o valor máximo a 100,00
  if (numero > 100) {
    numero = 100;
  }

  // Formata como string no padrão 15000,00 (sem separador de milhar)
  return numero
    .toFixed(2) // Garante duas casas decimais
    .replace('.', ','); // Troca ponto por vírgula para centavos
};

// Função para converter string formatada para número
export const parsearMoeda = (valorFormatado: string): number => {
  // Remove todos os caracteres que não são números, vírgula ou ponto
  const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
  // Substitui vírgula por ponto para conversão
  const numeroComPonto = numeroLimpo.replace(',', '.');
  const numero = parseFloat(numeroComPonto) || 0;
  return numero;
};

export function formatarDataRelativa(dataString: string): string {
  const data = new Date(dataString);
  const agora = new Date();
  const diffEmMs = agora.getTime() - data.getTime();
  const diffEmDias = Math.floor(diffEmMs / (1000 * 60 * 60 * 24));

  if (diffEmDias === 0) {
    return 'Hoje';
  } else if (diffEmDias === 1) {
    return 'Ontem';
  } else if (diffEmDias > 1 && diffEmDias <= 7) {
    return `Há ${diffEmDias} dias`;
  } else {
    return formatarData(dataString);
  }
}

export function formatarDataLog(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Agora mesmo';
    } else if (diffInHours < 24) {
      return `Há ${diffInHours}h`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `Há ${diffInDays}d`;
    }
  } catch (error) {
    return 'Data desconhecida';
  }
}

export function formatarDataLog2(dateString: string): string {
  try {
    const date = new Date(dateString);
    const now = new Date();
    //const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    if (diffInMinutes < 5) {
      return 'Agora mesmo';
    } else if (diffInMinutes < 30) {
      return `Há menos de 30 minutos`;
    } else {
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  } catch (error) {
    return 'Data desconhecida';
  }
}

type PhoneFormatRule = {
  minDigits: number;
  maxDigits: number;
  format: (digits: string) => string;
};

const formatWithAreaCode = (digits: string, firstGroupSize: number, secondGroupSize: number): string => {
  if (!digits) return '';

  const areaCode = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (!rest) {
    return `(${areaCode}`;
  }

  const firstGroup = rest.slice(0, firstGroupSize);
  const secondGroup = rest.slice(firstGroupSize, firstGroupSize + secondGroupSize);

  if (!secondGroup) {
    return `(${areaCode}) ${firstGroup}`;
  }

  return `(${areaCode}) ${firstGroup}-${secondGroup}`;
};

const formatWithSpaces = (digits: string, groups: number[]): string => {
  if (!digits) return '';

  const parts: string[] = [];
  let offset = 0;

  for (const groupSize of groups) {
    if (offset >= digits.length) {
      break;
    }

    const part = digits.slice(offset, offset + groupSize);
    if (part) {
      parts.push(part);
    }
    offset += groupSize;
  }

  return parts.join(' ');
};

const PHONE_RULES: Record<string, PhoneFormatRule> = {
  '55': {
    minDigits: 10,
    maxDigits: 11,
    format: (digits) => {
      if (digits.length <= 10) {
        return formatWithAreaCode(digits, 4, 4);
      }

      return formatWithAreaCode(digits, 5, 4);
    },
  },
  '1': {
    minDigits: 10,
    maxDigits: 10,
    format: (digits) => {
      if (!digits) return '';

      const areaCode = digits.slice(0, 3);
      const rest = digits.slice(3);

      if (!rest) {
        return `(${areaCode}`;
      }

      const firstGroup = rest.slice(0, 3);
      const secondGroup = rest.slice(3, 7);

      if (!secondGroup) {
        return `(${areaCode}) ${firstGroup}`;
      }

      return `(${areaCode}) ${firstGroup}-${secondGroup}`;
    },
  },
  '33': {
    minDigits: 9,
    maxDigits: 9,
    format: (digits) => formatWithSpaces(digits, [1, 2, 2, 2, 2]),
  },
  '34': {
    minDigits: 9,
    maxDigits: 9,
    format: (digits) => formatWithSpaces(digits, [3, 3, 3]),
  },
  '351': {
    minDigits: 9,
    maxDigits: 9,
    format: (digits) => formatWithSpaces(digits, [3, 3, 3]),
  },
  '44': {
    minDigits: 10,
    maxDigits: 11,
    format: (digits) => formatWithSpaces(digits, [5, 6]),
  },
};

const DEFAULT_PHONE_RULE: PhoneFormatRule = {
  minDigits: 10,
  maxDigits: 11,
  format: (digits) => {
    if (digits.length <= 10) {
      return formatWithAreaCode(digits, 4, 4);
    }

    return formatWithAreaCode(digits, 5, 4);
  },
};

export const getPhoneRuleByDdi = (ddi?: string): PhoneFormatRule => {
  if (!ddi) return DEFAULT_PHONE_RULE;

  return PHONE_RULES[ddi] || DEFAULT_PHONE_RULE;
};

export const formatarPhone = (value: string, ddi?: string): string => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '');
  const rule = getPhoneRuleByDdi(ddi);

  return rule.format(cleanValue.slice(0, rule.maxDigits));
};

//formatação de telefone durante a digitação em um campo de texto
export const formatarPhoneOnInput = (
  value: string,
  maxDigitsOrDdi: number | string = 11,
  ddi?: string,
): string => {
  if (!value) return '';

  const phoneDdi = typeof maxDigitsOrDdi === 'string' ? maxDigitsOrDdi : (ddi || '55');
  const maxDigits = typeof maxDigitsOrDdi === 'string'
    ? getPhoneRuleByDdi(maxDigitsOrDdi).maxDigits
    : maxDigitsOrDdi;

  // Remove tudo que não é dígito e limita ao máximo de dígitos
  const onlyDigits = value.replace(/\D/g, '').slice(0, maxDigits);

  // Aplica formatação
  return formatarPhone(onlyDigits, phoneDdi);
};

const formatarCPF = (value: string): string => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '');

  if (cleanValue.length === 11) {
    return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  return value;
};

const formatarCNPJ = (value: string): string => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '');

  if (cleanValue.length === 14) {
    return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  return value;
};

export const formatarCPFCNPJ = (value: string): string => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '');

  if (cleanValue.length === 11) {
    return formatarCPF(cleanValue);
  } else if (cleanValue.length === 14) {
    return formatarCNPJ(cleanValue);
  }

  return value;
};

export const formatarCPFOnInput = (value: string): string => {
  if (!value) return '';

  // Remove tudo que não é dígito e limita a 11 dígitos
  const onlyDigits = value.replace(/\D/g, '').slice(0, 11);

  // Aplica formatação
  return formatarCPF(onlyDigits);
};

/**
 * Formata um CNPJ durante a digitação (otimizada para uso em onChange)
 * @param value - Valor atual do campo
 * @returns CNPJ formatado
 */
export const formatarCNPJOnInput = (value: string): string => {
  if (!value) return '';

  // Remove tudo que não é dígito e limita a 14 dígitos
  const onlyDigits = value.replace(/\D/g, '').slice(0, 14);

  // Aplica formatação
  return formatarCNPJ(onlyDigits);
};

export const formatarCPFCNPJOnInput = (value: string, maxDigits: number = 14): string => {
  if (!value) return '';

  // Remove tudo que não é dígito e limita ao máximo de dígitos
  const onlyDigits = value.replace(/\D/g, '').slice(0, maxDigits);

  // Aplica formatação
  return formatarCPFCNPJ(onlyDigits);
};

// Função para formatar CEP durante a digitação em um campo de texto
export const formatarCepOnInput = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 5) {
    return numbers;
  }
  return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
};

/**
 * Interface para os dados retornados pela API do ViaCEP
 */
export interface CepData {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  erro?: boolean;
}

/**
 * Função para buscar dados do CEP na API do ViaCEP
 * @param cep - CEP a ser buscado (com ou sem formatação)
 * @param setValue - Função para atualizar os valores do formulário
 * @param setError - Função para definir mensagens de erro
 * @param setCepLoading - Função para controlar o estado de carregamento
 * @returns Promise<void>
 */
export const buscarCep = async (
  cep: string,
  setValue: (field: any, value: any) => void,
  setError: (error: string | null) => void,
  setCepLoading: (loading: boolean) => void
): Promise<void> => {
  if (!cep || cep.length < 8) {
    setError('Digite um CEP válido para buscar');
    return;
  }

  try {
    setCepLoading(true);
    setError(null);

    // Remove formatação do CEP para fazer a busca
    const cepNumeros = cep.replace(/\D/g, '');

    const response = await fetch(`https://viacep.com.br/ws/${cepNumeros}/json/`);
    const data: CepData = await response.json();

    if (data.erro) {
      setError('CEP não encontrado');
      return;
    }

    // Preenche os campos automaticamente
    setValue('endereco', data.logradouro || '');
    setValue('bairro', data.bairro || '');
    setValue('cidade', data.localidade || '');
    setValue('codigo_municipio', data.ibge || '');
    setValue('estado', data.uf || '');

    // Exibe mensagem de sucesso
    setError(null);

  } catch (err) {
    console.error('Erro ao buscar CEP:', err);
    setError('Erro ao buscar CEP. Verifique sua conexão com a internet.');
  } finally {
    setCepLoading(false);
  }
};

export const formatarMacAddress = (mac: string): string => {
  if (!mac) return '';
  return mac.replace(/(\w{2})(\w{2})(\w{2})(\w{2})(\w{2})(\w{2})/, '$1:$2:$3:$4:$5:$6');
};

export const formatarMacAddressOnInput = (mac: string): string => {
  if (!mac) return '';
  // Remove tudo que não é hexadecimal e limita em 12 caracteres
  const limpo = mac.replace(/[^a-fA-F0-9]/g, '').slice(0, 12);
  // Adiciona os dois pontos a cada dois caracteres
  return limpo
    .replace(/^(.{2})(.{0,2})(.{0,2})(.{0,2})(.{0,2})(.{0,2}).*/, (match, p1, p2, p3, p4, p5, p6) => {
      return [p1, p2, p3, p4, p5, p6].filter(Boolean).join(':');
    });
};

export const formatarIp = (ip: string): string => {
  if (!ip) return '';
  return ip.replace(/\./g, '.');
};

export const formatarIpOnInput = (ip: string): string => {
  if (!ip) return '';
  // Remove caracteres inválidos (apenas números e pontos são permitidos)
  let limpo = ip.replace(/[^0-9.]/g, '');
  // Divide em blocos por ponto
  const partes = limpo.split('.');
  // Limita para máximo 4 blocos
  const partesLimitadas = partes.slice(0, 4);
  // Para cada bloco, limita a 3 dígitos
  for (let i = 0; i < partesLimitadas.length; i++) {
    partesLimitadas[i] = partesLimitadas[i].slice(0, 3);
  }
  // Remonta o IP com pontos
  let novoIp = partesLimitadas.join('.');
  // Permite múltiplos pontos (por exemplo, se o usuário digita "192.")
  // Se o input original termina com ponto, adiciona um ponto ao final
  if (
    limpo.endsWith('.') &&
    partesLimitadas.length < 4 &&
    // Evitar adicionar ponto se acabou de criar bloco vazio
    !novoIp.endsWith('.')
  ) {
    novoIp += '.';
  }
  return novoIp;
};

